import { Badge } from '@neo/primitives/badge'
import { pluralize } from '@shared/utils'
import { useMutationState } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useParams } from 'router-v6'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { useFlowStore } from '../store'
import type { FlowData, GenericNode } from '../types'
import { processValidationErrors } from '../utils/validation-error-processor'
import { ValidationErrorPopover } from './validation-error-popover'

interface ValidationErrorIndicatorProps {
  flowData: FlowData
}

export const ValidationErrorIndicator = ({ flowData }: ValidationErrorIndicatorProps) => {
  const { id } = useParams()
  const flowDataErrors = flowData?.validation_errors || []
  const isPopoverOpen = useFlowStore((state) => state.isValidationErrorPopoverOpen)
  const toggleValidationErrorPopover = useFlowStore((state) => state.toggleValidationErrorPopover)
  const { getNodes } = useTypedReactFlow()

  // Get current nodes for error processing
  const currentNodes = getNodes() as GenericNode[]

  // Get validation errors from mutation state
  const mutationStates = useMutationState({
    filters: {
      mutationKey: flowQueryKeys().flowData(id || 'new'),
      predicate: (mutation) => mutation.state.status !== 'pending',
    },
    select: (mutation) => ({
      error: mutation.state.error,
      variables: mutation.state.variables,
    }),
  })

  // Process the latest mutation error if exists
  const latestMutation = mutationStates[mutationStates.length - 1]
  const latestMutationError = latestMutation?.error || null
  const latestMutationVariables = latestMutation?.variables as {
    payload: unknown
    nodeMap?: Record<string, string>
  }

  // Extract nodeMap from mutation variables
  const nodeMap = latestMutationVariables?.nodeMap || null

  const latestMutationErrors = latestMutationError
    ? processValidationErrors(latestMutationError, currentNodes, undefined, nodeMap)
    : null

  // Use mutation errors if present, otherwise use flow data errors
  const errors =
    latestMutationErrors && latestMutationErrors.length > 0 ? latestMutationErrors : flowDataErrors

  // Don't render if no errors
  if (errors.length === 0) return null

  return (
    <ValidationErrorPopover
      errors={errors}
      isOpen={isPopoverOpen}
      onClose={toggleValidationErrorPopover}
      trigger={
        <button
          type="button"
          className="cursor-pointer h-full flex items-center bg-transparent p-0 ml-1"
          onClick={toggleValidationErrorPopover}
        >
          <Badge variant="destructive" className="h-full py-0.5">
            <AlertTriangle className="h-4 w-4 mr-1" />
            <span className="font-jetbrains text-xs-500">{pluralize(errors.length, 'Error')}</span>
          </Badge>
        </button>
      }
    />
  )
}
